import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './LayerSettingsModal.css';

interface LayerSettings {
  default_rotation: number;
  auto_rotate: boolean;
  files: { [key: string]: number };
  coordinate_system: string;
  apply_mode: "override" | "default";
  override_crs: string | null;
}

interface LayerSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  layerId: string;
  layerName: string;
  files: any[];
  onSaveSettings: (settings: LayerSettings) => void;
}

const LayerSettingsModal: React.FC<LayerSettingsModalProps> = ({
  isOpen,
  onClose,
  layerId,
  layerName,
  files,
  onSaveSettings
}) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<LayerSettings>({
    default_rotation: 0,
    auto_rotate: false,
    files: {},
    coordinate_system: "EPSG:4326",
    apply_mode: "default",
    override_crs: null
  });
  const [loading, setLoading] = useState(false);

  // 설정 로드
  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen, layerId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/upload/layers/local/${layerId}/layer-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('레이어 설정 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/upload/layers/local/${layerId}/layer-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        const result = await response.json();
        onSaveSettings(settings);
        
        // 캐시가 무효화된 경우 사용자에게 알림
        if (result.cache_invalidated) {
          console.log('회전 설정이 변경되어 이미지가 새로고침됩니다.');
        }
        
        onClose();
      } else {
        alert('설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('설정 저장 실패:', error);
      alert('설정 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileRotationChange = (fileName: string, angle: number) => {
    setSettings(prev => ({
      ...prev,
      files: {
        ...prev.files,
        [fileName]: angle
      }
    }));
  };

  const handleApplyToAll = () => {
    const newFiles: { [key: string]: number } = {};
    files.forEach(file => {
      newFiles[file.name] = settings.default_rotation;
    });
    
    setSettings(prev => ({
      ...prev,
      files: newFiles
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="rotation-settings-modal-overlay">
      <div className="rotation-settings-modal">
        <div className="modal-header">
          <h3>{t('layers.layerSettings')} - {layerName}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              {/* 좌표계 설정 */}
              <div className="settings-section">
                <h4>{t('layers.coordinateSystem')}</h4>
                <div className="setting-item">
                  <label>{t('layers.coordinateSystem')}:</label>
                  <select
                    value={settings.coordinate_system}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      coordinate_system: e.target.value,
                      override_crs: e.target.value 
                    }))}
                  >
                    <option value="EPSG:4326">WGS84 (EPSG:4326)</option>
                    <option value="EPSG:3857">Web Mercator (EPSG:3857)</option>
                    <option value="EPSG:2154">Lambert-93 (EPSG:2154)</option>
                    <option value="EPSG:5186">Korea 2000 / Central Belt (EPSG:5186)</option>
                    <option value="EPSG:5179">Korea 2000 / Central Belt 2010 (EPSG:5179)</option>
                  </select>
                </div>
                <div className="setting-item">
                  <label>{t('common.apply')} {t('common.mode')}:</label>
                  <select
                    value={settings.apply_mode}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      apply_mode: e.target.value as "override" | "default"
                    }))}
                  >
                    <option value="default">{t('common.default')} {t('common.apply')} {t('common.mode')}</option>
                    <option value="override">{t('common.override')} {t('common.apply')} {t('common.mode')}</option>
                  </select>
                </div>
                <div className="setting-item">
                  <label className="help-text">
                    • {t('layers.defaultApplyDescription')}<br/>
                    • {t('layers.overrideApplyDescription')}
                  </label>
                </div>
              </div>

              {/* 회전 설정 */}
              <div className="settings-section">
                <h4>{t('layers.rotation')}</h4>
                <div className="setting-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={settings.auto_rotate}
                      onChange={(e) => setSettings(prev => ({ ...prev, auto_rotate: e.target.checked }))}
                    />
                    {t('layers.autoRotate')}
                  </label>
                </div>
                <div className="setting-item">
                  <label>{t('layers.manualRotation')}:</label>
                  <select
                    value={settings.default_rotation}
                    onChange={(e) => setSettings(prev => ({ ...prev, default_rotation: parseInt(e.target.value) }))}
                  >
                    <option value={0}>{t('layers.noRotation')}</option>
                    <option value={90}>90° {t('layers.counterclockwise')}</option>
                    <option value={180}>180°</option>
                    <option value={270}>90° {t('layers.clockwise')}</option>
                  </select>
                  <button className="apply-all-btn" onClick={handleApplyToAll}>
                    {t('common.applyToAll')}
                  </button>
                </div>
              </div>

              {/* 개별 파일 설정 */}
              <div className="settings-section">
                <h4>{t('layers.individualFileSettings')}</h4>
                <div className="files-list">
                  {files.map((file, index) => (
                    <div key={index} className="file-setting-item">
                      <span className="file-name">{file.name}</span>
                      <select
                        value={settings.files[file.name] || 0}
                        onChange={(e) => handleFileRotationChange(file.name, parseInt(e.target.value))}
                      >
                        <option value={0}>{t('layers.noRotation')}</option>
                        <option value={90}>90° {t('layers.counterclockwise')}</option>
                        <option value={180}>180°</option>
                        <option value={270}>90° {t('layers.clockwise')}</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="save-btn" onClick={handleSave} disabled={loading}>
            {loading ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LayerSettingsModal; 